import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, Crown, CreditCard, Lock, PackageOpen, PlusCircle, RefreshCw, Wallet } from 'lucide-react';
import s from './InvoiceSummary.module.css';
import { EXPIRY_WARN_DAYS, LOW_BALANCE_LIMIT, daysLeft, isCardUsable } from '../../Membershippricing';
import useAccess from '../../../../hooks/useAccess';

const money = (v) => Number(v || 0).toFixed(2);
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const last4 = (num) => String(num || '').replace(/\s+/g, '').slice(-4) || '••••';
const withParams = (base, params) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

/** Small on / off switch */
const Toggle = ({ checked, onChange, label }) => (
    <span className={s.switch}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
        <span />
    </span>
);

const InvoiceSummary = ({
    customer = {},
    payments = {},
    setPayments,
    overallDiscount = {},
    setOverallDiscount,
    isRoundOff,
    setIsRoundOff,
    totals = {},
    hasDiscountAccess,
    discountBlockedReason = '',
    customerWalletBalance = 0,
    customerDueAmount = 0,
    customerPackages = [],
    prepaidCards = [],
    memberships = [],
    appliedMembership = null,
    onMembershipChange,
    membershipDeskPath = '/renew-loyalty',
    isEditingCompleted = false,
    maxDiscountPercent = 100,
}) => {
    const { can, canAny, isOwner } = useAccess();
    // Live access (the server checks the same rules)
    const canSplit = can('billing.split_payment');
    const canUseBalance = can('billing.use_wallet');
    const canCollectDue = can('billing.collect_due');
    const canCredit = can('billing.credit_sale');
    const canRenew = isOwner || canAny(['billing.create', 'memberships.renew', 'memberships.sell_membership']);

    const safePayments = { cash: '', card: '', upi: '', wallet: '', prepaidCards: [], prepaidCardAmount: 0, ...payments };
    const safeDiscount = { reasonSelect: '', customReason: '', value: '', type: '%', ...overallDiscount };

    const billTotal = Number(totals.grandTotal || 0);
    const serviceOnlyTotal = Number(totals.serviceOnlyTotal || 0);
    const sumTax = Number(totals.sumTax || 0);
    const roundOffAmount = Number(totals.roundOffAmt || 0);
    const itemDiscount = Number(totals.sumItemDiscount || 0);
    const overallDiscountAmount = Number(totals.finalOverallDisc || 0);

    // Cards already used on this bill (editing a completed bill: their money is still "on" this bill)
    const alreadyUsedRef = useRef(
        isEditingCompleted
            ? Object.fromEntries((payments.prepaidCards || []).map((c) => [String(c.cardId), Number(c.amount) || 0]))
            : {}
    );

    const [applyPreviousDue, setApplyPreviousDue] = useState(false);
    const [applyWallet, setApplyWallet] = useState(Number(payments.wallet) > 0);
    const walletOn = applyWallet && canUseBalance;
    const dueOn = applyPreviousDue && canCollectDue;
    const [selectedCardIds, setSelectedCardIds] = useState(() => (payments.prepaidCards || []).map((c) => String(c.cardId)));
    const autoPickedRef = useRef((payments.prepaidCards || []).length > 0 || isEditingCompleted);

    /* ------------------------------ Cards ------------------------------ */
    const { usableCards, expiredCards } = useMemo(() => {
        const used = alreadyUsedRef.current;
        const usable = [];
        const expired = [];
        prepaidCards.forEach((card) => {
            const extra = used[card.id] || 0;
            if (isCardUsable(card) || extra > 0) usable.push({ ...card, available: round2(card.balance + extra) });
            else if (card.status === 'EXPIRED' || (daysLeft(card.expiryDate) ?? 1) <= 0) expired.push({ ...card, reason: 'EXPIRED' });
            else if (card.status === 'ASSIGNED' && card.balance <= 0) expired.push({ ...card, reason: 'EMPTY' });
        });
        // the card that expires first, then the smaller balance
        usable.sort((a, b) => (daysLeft(a.expiryDate) ?? 99999) - (daysLeft(b.expiryDate) ?? 99999) || a.available - b.available);
        return { usableCards: usable, expiredCards: expired };
    }, [prepaidCards]);

    // First time: pick as many cards as needed to cover the services
    useEffect(() => {
        if (autoPickedRef.current || !canUseBalance || !usableCards.length || serviceOnlyTotal <= 0) return;
        let need = serviceOnlyTotal;
        const picked = [];
        for (const card of usableCards) {
            if (need <= 0) break;
            picked.push(card.id);
            need = round2(need - card.available);
        }
        autoPickedRef.current = true;
        setSelectedCardIds(picked);
    }, [usableCards, serviceOnlyTotal, canUseBalance]);

    // How much each selected card pays (in the order shown)
    const allocation = useMemo(() => {
        let remaining = serviceOnlyTotal;
        const byCard = {};
        usableCards.forEach((card) => {
            if (!canUseBalance || !selectedCardIds.includes(card.id)) return;
            const amount = round2(Math.max(0, Math.min(card.available, remaining)));
            byCard[card.id] = amount;
            remaining = round2(remaining - amount);
        });
        const list = usableCards.filter((c) => (byCard[c.id] || 0) > 0).map((c) => ({ cardId: c.id, amount: byCard[c.id] }));
        const total = round2(list.reduce((sum, c) => sum + c.amount, 0));
        return { byCard, list, total, uncovered: round2(Math.max(0, serviceOnlyTotal - total)) };
    }, [usableCards, selectedCardIds, serviceOnlyTotal, canUseBalance]);

    const toggleCard = (cardId) => setSelectedCardIds((ids) => (ids.includes(cardId) ? ids.filter((id) => id !== cardId) : [...ids, cardId]));

    /* ------------------------------ Push to payments ------------------------------ */
    useEffect(() => {
        const prepaidSum = allocation.total;
        const targetForKhata = Math.max(0, billTotal - prepaidSum + (dueOn ? customerDueAmount : 0));
        const usedKhata = walletOn ? round2(Math.min(customerWalletBalance, targetForKhata)) : 0;
        setPayments((prev) => {
            const same = JSON.stringify(prev.prepaidCards || []) === JSON.stringify(allocation.list)
                && round2(prev.prepaidCardAmount) === prepaidSum
                && round2(prev.wallet) === usedKhata;
            if (same) return prev;
            return { ...prev, prepaidCards: allocation.list, prepaidCardAmount: prepaidSum, prepaidCardId: allocation.list[0]?.cardId || null, wallet: usedKhata };
        });
    }, [allocation, walletOn, dueOn, billTotal, customerWalletBalance, customerDueAmount, setPayments]);

    /* ------------------------------ Totals ------------------------------ */
    const prepaidUsed = Number(safePayments.prepaidCardAmount) || 0;
    const walletUsed = Number(safePayments.wallet) || 0;
    const netPayable = round2(billTotal + (dueOn ? customerDueAmount : 0) - walletUsed - prepaidUsed);
    const currentPaid = (Number(safePayments.cash) || 0) + (Number(safePayments.card) || 0) + (Number(safePayments.upi) || 0);
    const difference = currentPaid - netPayable;

    // Without split payment only one of cash / card / UPI can be used
    const handlePayChange = (field, value) => setPayments((prev) => {
        const next = { ...prev, [field]: value };
        if (!canSplit && Number(value) > 0) ['cash', 'card', 'upi'].forEach((f) => { if (f !== field) next[f] = ''; });
        return next;
    });
    const handleDiscountChange = (field, value) => setOverallDiscount((prev) => ({ ...prev, [field]: value }));
    const handleAutoFill = (field) => {
        const others = canSplit ? currentPaid - (Number(safePayments[field]) || 0) : 0;
        const remaining = Math.max(0, netPayable - others);
        if (remaining > 0) handlePayChange(field, remaining);
    };

    const appliedId = appliedMembership ? appliedMembership.id : 'NONE';
    const showCards = canUseBalance && (usableCards.length > 0 || expiredCards.length > 0);
    const hasBenefits = (customerDueAmount > 0 && canCollectDue) || (customerWalletBalance > 0 && canUseBalance) || customerPackages.length > 0 || showCards;
    const unselectedUsable = usableCards.filter((c) => !selectedCardIds.includes(c.id));
    const deskLink = (action, target) => withParams(membershipDeskPath, { phone: customer?.phone, customerId: customer?.customerId, action, kind: 'PREPAID', target });

    const status = billTotal <= 0 && netPayable <= 0 ? null
        : difference < -0.01 ? { cls: s.stDue, icon: AlertCircle, text: `₹${Math.abs(difference).toFixed(2)} due`, sub: canCredit ? 'Goes to khata' : 'Collect full amount' }
            : difference > 0.01 ? { cls: s.stAdv, icon: Wallet, text: `₹${difference.toFixed(2)} advance`, sub: 'Saved to khata' }
                : { cls: s.stOk, icon: CheckCircle2, text: netPayable > 0 ? 'Settled' : 'Paid by benefits', sub: '' };

    return (
        <div className={s.wrap}>
            <div className={s.scroll}>
                {/* ---------------- Membership discount ---------------- */}
                {memberships.length > 0 && (
                    <section className={s.block}>
                        <h4 className={s.title}><Crown size={12} /> Membership</h4>
                        <div className={s.chips} role="radiogroup" aria-label="Membership discount">
                            {memberships.map((m) => {
                                const left = daysLeft(m.expiryDate);
                                const on = appliedId === m.id;
                                return (
                                    <button type="button" key={m.id} role="radio" aria-checked={on} className={`${s.chip} ${on ? s.chipOn : ''}`} onClick={() => onMembershipChange?.(m.id)}
                                        title={`${m.serviceDiscount}% on services${m.productDiscount ? `, ${m.productDiscount}% on products` : ''}`}>
                                        {on && <Check size={11} />} {m.name} <b>{m.serviceDiscount}%</b>
                                        {left !== null && left <= EXPIRY_WARN_DAYS && <em className={s.tagWarn}>{left <= 1 ? 'today' : `${left}d`}</em>}
                                    </button>
                                );
                            })}
                            <button type="button" role="radio" aria-checked={appliedId === 'NONE'} className={`${s.chip} ${appliedId === 'NONE' ? s.chipOn : ''}`} onClick={() => onMembershipChange?.('NONE')}>
                                None
                            </button>
                        </div>
                        {isEditingCompleted && <p className={s.hint}>Applies to new lines only.</p>}
                    </section>
                )}

                {/* ---------------- Totals ---------------- */}
                <section className={s.block}>
                    <h4 className={s.title}>Summary</h4>
                    <div className={s.row}><span>Gross</span><span>₹{money(totals.sumGross)}</span></div>
                    {itemDiscount > 0 && <div className={`${s.row} ${s.good}`}><span>Item discount</span><span>−₹{money(itemDiscount)}</span></div>}
                    {sumTax > 0 && <div className={s.row}><span>{totals.isGstBill ? 'GST' : 'Tax'}</span><span>+₹{money(sumTax)}</span></div>}
                    {overallDiscountAmount > 0 && <div className={`${s.row} ${s.good}`}><span>Bill discount</span><span>−₹{money(overallDiscountAmount)}</span></div>}

                    {!hasDiscountAccess && (
                        <p className={s.locked} title={discountBlockedReason}>
                            <Lock size={11} /> Bill discount is off. {discountBlockedReason}
                        </p>
                    )}

                    {hasDiscountAccess && (
                        <div className={s.discount}>
                            <select className={s.select} value={safeDiscount.reasonSelect} aria-label="Discount reason"
                                onChange={(e) => { handleDiscountChange('reasonSelect', e.target.value); if (e.target.value !== 'Custom') handleDiscountChange('customReason', ''); }}>
                                <option value="">Discount reason</option>
                                <option value="Festival Offer">Festival offer</option>
                                <option value="Membership">Membership</option>
                                <option value="Custom">Other</option>
                            </select>
                            <div className={s.combo}>
                                <input type="number" min="0" max={safeDiscount.type === '%' ? maxDiscountPercent : undefined} placeholder="0" aria-label="Discount value" value={safeDiscount.value === 0 ? '' : safeDiscount.value} onChange={(e) => handleDiscountChange('value', e.target.value)} />
                                <select value={safeDiscount.type} aria-label="Discount type" onChange={(e) => handleDiscountChange('type', e.target.value)}><option value="%">%</option><option value="₹">₹</option></select>
                            </div>
                            {maxDiscountPercent < 100 && <small className={s.max}>max {maxDiscountPercent}%</small>}
                        </div>
                    )}

                    <label className={s.row}>
                        <span className={s.inline}><Toggle checked={Boolean(isRoundOff)} onChange={setIsRoundOff} label="Round off" /> Round off</span>
                        <span>{roundOffAmount > 0 ? '+' : ''}{money(roundOffAmount)}</span>
                    </label>
                </section>

                {/* ---------------- Benefits & khata ---------------- */}
                {hasBenefits && (
                    <section className={s.block}>
                        <h4 className={s.title}><Wallet size={12} /> Client benefits</h4>

                        {customerDueAmount > 0 && canCollectDue && (
                            <label className={s.row}>
                                <span className={s.inline}><Toggle checked={applyPreviousDue} onChange={setApplyPreviousDue} label="Settle old due" /> Settle old due</span>
                                <span className={s.bad}>+₹{money(customerDueAmount)}</span>
                            </label>
                        )}

                        {customerWalletBalance > 0 && canUseBalance && (
                            <label className={s.row}>
                                <span className={s.inline}><Toggle checked={applyWallet} onChange={setApplyWallet} label="Use khata advance" /> Khata advance <small>₹{money(customerWalletBalance)}</small></span>
                                <span className={walletOn && walletUsed > 0 ? s.bad : s.muted}>{walletOn && walletUsed > 0 ? `−₹${money(walletUsed)}` : 'off'}</span>
                            </label>
                        )}

                        {showCards && (
                            <div className={s.cards}>
                                <div className={s.cardsHead}>
                                    <span><CreditCard size={11} /> Prepaid (services)</span>
                                    {allocation.total > 0 && <span>{inr(allocation.total)} of {inr(serviceOnlyTotal)}</span>}
                                </div>
                                {serviceOnlyTotal <= 0 && usableCards.length > 0 && <p className={s.hint}>Add a service to use a card.</p>}

                                {usableCards.map((card) => {
                                    const selected = selectedCardIds.includes(card.id);
                                    const using = allocation.byCard[card.id] || 0;
                                    const left = daysLeft(card.expiryDate);
                                    const expiringSoon = left !== null && left <= EXPIRY_WARN_DAYS;
                                    const lowBalance = card.available < LOW_BALANCE_LIMIT;
                                    const canPick = serviceOnlyTotal > 0;
                                    return (
                                        <div
                                            key={card.id}
                                            role="button"
                                            tabIndex={canPick ? 0 : -1}
                                            aria-pressed={selected}
                                            aria-disabled={!canPick}
                                            className={`${s.card} ${selected ? s.cardOn : ''} ${canPick ? '' : s.cardOff}`}
                                            onClick={() => canPick && toggleCard(card.id)}
                                            onKeyDown={(e) => { if (canPick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleCard(card.id); } }}
                                            title={`Valid till ${fmtDate(card.expiryDate)}`}
                                        >
                                            <span className={s.check}>{selected && <Check size={10} />}</span>
                                            <span className={s.cardMain}>
                                                <strong>{card.name} <i>••{last4(card.number)}</i></strong>
                                                <small>
                                                    {inr(card.available)}
                                                    {lowBalance && <em className={s.tagWarn}>low</em>}
                                                    {expiringSoon && <em className={s.tagWarn}>{left <= 1 ? 'ends today' : `${left}d left`}</em>}
                                                </small>
                                            </span>
                                            <span className={s.cardEnd}>
                                                {using > 0 ? <b className={s.bad}>−{inr(using)}</b> : <small>{selected ? 'not needed' : 'use'}</small>}
                                                {canRenew && (
                                                    <a href={deskLink('RECHARGE', card.id)} target="_blank" rel="noreferrer" className={`${s.iconLink} ${lowBalance ? s.iconLinkStrong : ''}`}
                                                        onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} title={`Recharge ${card.name}`} aria-label={`Recharge ${card.name}`}>
                                                        <PlusCircle size={13} />
                                                    </a>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}

                                {allocation.total > 0 && allocation.uncovered > 0 && (
                                    <p className={s.warn}><AlertCircle size={12} /> {inr(allocation.uncovered)} not covered{unselectedUsable.length > 0 ? ', pick another card' : ''}.</p>
                                )}

                                {expiredCards.map((card) => (
                                    <div key={card.id} className={s.expired}>
                                        <span>
                                            <strong>{card.name} <i>••{last4(card.number)}</i></strong>
                                            <small>{card.reason === 'EMPTY' ? 'No balance' : `Expired ${fmtDate(card.expiryDate)}`}</small>
                                        </span>
                                        {canRenew && (
                                            <a href={deskLink(card.reason === 'EMPTY' ? 'RECHARGE' : 'RENEW', card.id)} target="_blank" rel="noreferrer" className={s.textLink}>
                                                {card.reason === 'EMPTY' ? <><PlusCircle size={11} /> Recharge</> : <><RefreshCw size={11} /> Renew</>}
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {customerPackages.filter((pkg) => (pkg.status || 'ACTIVE') === 'ACTIVE').map((pkg) => (
                            <div key={pkg.id || pkg._id || pkg.name} className={s.pkg}>
                                <div className={s.pkgHead}><PackageOpen size={11} /> {pkg.name || pkg.packageName}</div>
                                {(pkg.services || []).map((srv) => (
                                    <div className={s.row} key={srv.key || srv.itemName}>
                                        <span>{srv.name || srv.itemName}</span>
                                        <span className={s.accent}>{srv.left ?? Math.max(0, (srv.totalQty || 0) - (srv.usedQty || 0))}/{srv.total ?? srv.totalQty} left</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </section>
                )}

                {/* ---------------- Payment ---------------- */}
                <section className={s.block}>
                    <h4 className={s.title}>Payment {!canSplit && <small>one mode</small>}</h4>
                    <div className={s.pay}>
                        {['cash', 'card', 'upi'].map((field) => (
                            <label className={s.payField} key={field}>
                                <span>
                                    {field === 'upi' ? 'UPI' : field[0].toUpperCase() + field.slice(1)}
                                    <button type="button" className={s.maxBtn} onClick={() => handleAutoFill(field)}>max</button>
                                </span>
                                <input type="number" min="0" placeholder="0" value={safePayments[field] === 0 ? '' : safePayments[field]} onChange={(e) => handlePayChange(field, e.target.value)} />
                            </label>
                        ))}
                    </div>
                </section>
            </div>

            {/* ---------------- Footer ---------------- */}
            <footer className={s.foot}>
                {(prepaidUsed > 0 || walletUsed > 0) && (
                    <div className={s.row}>
                        <span>Paid by {[prepaidUsed > 0 && 'cards', walletUsed > 0 && 'khata'].filter(Boolean).join(' + ')}</span>
                        <span>−₹{money(prepaidUsed + walletUsed)}</span>
                    </div>
                )}
                <div className={s.net}>
                    <span>Net payable</span>
                    <strong>₹{money(netPayable)}</strong>
                </div>
                {status && (
                    <div className={`${s.status} ${status.cls}`}>
                        <status.icon size={13} />
                        <b>{status.text}</b>
                        {status.sub && <span>{status.sub}</span>}
                    </div>
                )}
            </footer>
        </div>
    );
};

export default InvoiceSummary;